/**
 * Composable for setting up drag-to-scroll functionality on the timeline or table header
*/

/* eslint-disable no-use-before-define */
import { onMounted, onUnmounted } from 'vue';

export default function useDrag2Scroll(scrollElementSelector = '#table-wrapper', triggerElementSelector = null) {
  if (! triggerElementSelector) triggerElementSelector = scrollElementSelector;
  let scrollElement = null;
  let triggerElement = null;
  let initialCursor = null;
  const pos = { left: 0, x: 0, velX: 0, time: 0 };
  let momentumID = null;
  let isDragging = false;
  const dragThreshold = 6; // px before we consider movement a drag (and not a click)

  function mouseDownHandler(e) {
    if (e.button) return; // left click only

    initialCursor = triggerElement.style.cursor;
    triggerElement.style.cursor = 'grabbing';
    triggerElement.style.userSelect = 'none';

    _.extend(pos, {
      left: scrollElement.scrollLeft,
      x: e.clientX, // current mouse position
      velX: 0,
    });

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  }

  function mouseMoveHandler(e) {
    const dx = e.clientX - pos.x; // how far the mouse has been moved

    if (! isDragging && Math.abs(dx) > dragThreshold) isDragging = true;

    // Scroll velocity
    pos.velX = (pos.left - dx) - scrollElement.scrollLeft;
    pos.time = Date.now();

    scrollElement.scrollLeft = pos.left - dx; // scroll
  }

  function mouseUpHandler() {
    triggerElement.style.cursor = initialCursor;
    triggerElement.style.removeProperty('user-select');

    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);

    if (isDragging) {
      // suppress the immediate click that follows the mouseup
      const preventClickOnce = (ev) => {
        ev.preventDefault();
        ev.stopImmediatePropagation();
        document.removeEventListener('click', preventClickOnce, true);
      };
      // use capture so we stop handlers (which often listen in bubble)
      document.addEventListener('click', preventClickOnce, true);
      // remove the listener after a short timeout if no click happens
      setTimeout(() => document.removeEventListener('click', preventClickOnce, true), 300);
    }

    if (Date.now() - pos.time < 300) beginMomentumTracking(); // start slowing down

    isDragging = false;
  }

  function beginMomentumTracking() {
    if (Math.abs(pos.velX) < 3) return;
    cancelMomentumTracking();
    momentumID = requestAnimationFrame(momentumLoop);

    // listen for mouse events to stop the drag momentum loop
    window.addEventListener('scroll', cancelMomentumTracking);
    window.addEventListener('mousedown', cancelMomentumTracking);
  }

  function cancelMomentumTracking() {
    cancelAnimationFrame(momentumID);
    window.removeEventListener('scroll', cancelMomentumTracking);
    window.removeEventListener('mousedown', cancelMomentumTracking);
  }

  function momentumLoop() {
    scrollElement.scrollLeft += pos.velX; // apply the velocity to the scroll position
    pos.velX *= 0.95; // slow the velocity slightly
    if (Math.abs(pos.velX) > 0.5) { // still moving ?
      momentumID = requestAnimationFrame(momentumLoop); // keep looping
    }
  }

  function initDrag2Scroll() {
    scrollElement = document.querySelector(scrollElementSelector);
    triggerElement = document.querySelector(triggerElementSelector);
    if (! scrollElement || ! triggerElement) return;
    triggerElement.addEventListener('mousedown', mouseDownHandler);
  }

  onMounted(initDrag2Scroll);

  onUnmounted(() => {
    if (triggerElement) triggerElement.removeEventListener('mousedown', mouseDownHandler);
    cancelMomentumTracking();
    document.removeEventListener('mousemove', mouseMoveHandler); // in case of unmount during drag
    document.removeEventListener('mouseup', mouseUpHandler);
  });

  return {
    initDrag2Scroll,
  };
}
